import { Empty, Space, Spin } from "antd";
import ReactApexChart from "react-apexcharts";

const CollectionZoneTimeline = ({
  collectionsData,
  error,
  appliedZones,
  selectedDate,
}) => {
  const calculateColorIntensity = (revenue, days) => {
    if (!Array.isArray(collectionsData) || collectionsData.length === 0) {
      return { collection: `rgba(24, 144, 255, 0.1)` };
    }

    const dailyRevenues = collectionsData.map((item) => {
      const endDate = item.end ? new Date(item.end) : new Date();
      const startDate = new Date(item.start);
      const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      return item.revenue / (days || 1);
    });

    const maxDailyRevenue = Math.max(...dailyRevenues);
    const minDailyRevenue = Math.min(...dailyRevenues);

    if (maxDailyRevenue === minDailyRevenue) {
      return { collection: `rgba(24, 144, 255, 0.5)` };
    }

    const dailyRevenue = revenue / (days || 1);

    const normalized =
      (dailyRevenue - minDailyRevenue) / (maxDailyRevenue - minDailyRevenue);

    const clampedNormalized = Math.max(0, Math.min(1, normalized));

    return {
      collection: `rgba(24, 144, 255, ${0.2 + clampedNormalized * 0.8})`,
    };
  };

  const processData = () => {
    let processedData = [];

    const filteredData = collectionsData
      .filter((item) => {
        if (appliedZones.includes("all")) return true;
        return appliedZones.includes(item.zone);
      })
      .map((item) => {
        const endDate = item.end || new Date().toISOString().split("T")[0];
        const days = Math.ceil(
          (new Date(endDate) - new Date(item.start)) / (1000 * 60 * 60 * 24)
        );
        const dailyRevenue = item.revenue / (days || 1);

        return {
          ...item,
          calculatedDays: days,
          calculatedDailyRevenue: dailyRevenue,
          calculatedEndDate: endDate,
        };
      })
      .sort((a, b) => b.calculatedDailyRevenue - a.calculatedDailyRevenue); // Ordenar pela maior média diária

    filteredData.forEach((item) => {
      const colors = calculateColorIntensity(item.revenue, item.calculatedDays);

      processedData.push({
        x: `${item.collection} (${item.zone_description})`,
        y: [
          new Date(item.start).getTime(),
          new Date(item.calculatedEndDate).getTime(),
        ],
        fillColor: colors.collection,
        revenue: item.revenue,
        dailyRevenue: item.calculatedDailyRevenue,
        zone: item.zone,
        zone_description: item.zone_description,
      });
    });

    return [
      {
        name: "Coleções",
        data: processedData,
      },
    ];
  };

  const getMonthRange = (date) => {
    if (!date) return null;
    const [year, month] = date.split("-");
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    return [startDate.getTime(), endDate.getTime()];
  };

  const options = {
    chart: {
      height: 350,
      type: "rangeBar",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "30px",
        rangeBarGroupRows: true,
      },
    },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM/yy",
          day: "dd/MMM",
        },
        offsetY: 3,
      },
      position: "top",
      axisTicks: {
        show: false,
      },
      min: selectedDate ? getMonthRange(selectedDate)[0] : undefined,
      max: selectedDate ? getMonthRange(selectedDate)[1] : undefined,
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "12px",
          colors: "#000000",
        },
        maxWidth: 300,
        minWidth: 150,
      },
      tickAmount: "dataPoints",
    },
    grid: {
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: false,
        },
      },
    },
    tooltip: {
      custom: function (opts) {
        const datapoint = opts.ctx.w.config.series[0].data[opts.dataPointIndex];
        const startDate = new Date(datapoint.y[0]);
        const endDate = new Date(datapoint.y[1]);
        const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));

        return `
          <div class="apexcharts-tooltip-title" style="padding: 8px">
            <b>${datapoint.x}</b><br/>
            Período: ${startDate.toLocaleDateString()} - ${
              datapoint.y[1] === new Date().getTime()
                ? "Atual"
                : endDate.toLocaleDateString()
            }<br/>
            Duração: ${days} dias<br/>
            Faturamento total: R$ ${datapoint.revenue.toLocaleString(
              "pt-BR"
            )}<br/>
            Média diária: R$ ${Math.round(
              datapoint.dailyRevenue
            ).toLocaleString("pt-BR")}/dia
            </div>
        `;
      },
    },
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const datapoint = opts.w.config.series[0].data[opts.dataPointIndex];
        return datapoint.zone_description;
      },
      style: {
        colors: ["#ffffff"],
        fontSize: "12px",
      },
      background: {
        enabled: true,
        foreColor: "#000000",
        padding: 4,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: "#ddd",
        opacity: 0.9,
      },
      position: "center",
      offsetX: 15,
    },
  };

  const calculateHeight = () => {
    const filteredData = collectionsData.filter((item) => {
      if (appliedZones.includes("all")) return true;
      return appliedZones.includes(item.zone);
    });
    return Math.max(350, filteredData.length * 50);
  };

  if (error) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span style={{ color: "#ff4d4f" }}>
            Erro ao carregar dados das coleções. Tente novamente mais tarde.
          </span>
        }
      />
    );
  }

  const hasDataForSelectedZones = collectionsData.some(
    (item) => appliedZones.includes("all") || appliedZones.includes(item.zone)
  );

  if (!hasDataForSelectedZones) {
    return (
      <Empty
        description="Nenhuma coleção encontrada para as zonas selecionadas"
        style={{ padding: "50px" }}
      />
    );
  }

  return (
    <div style={{ width: "100%" }}>
      <Space direction="vertical" style={{ width: "100%" }}>
        <ReactApexChart
          options={options}
          series={processData()}
          type="rangeBar"
          height={calculateHeight()}
        />
      </Space>
    </div>
  );
};

export default CollectionZoneTimeline;
